import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Stack,
  CircularProgress,
  alpha,
  useTheme,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { useApi } from '../../hooks/useApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function Audit({ filePath, onClose }) {
  const jApi = useApi();
  const theme = useTheme();

  // Get filePath from URL params if not provided as prop
  const urlParams = new URLSearchParams(window.location.search);
  const filePathFromUrl = urlParams.get('filePath');
  const actualFilePath = filePath || filePathFromUrl;
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [markdownContent, setMarkdownContent] = useState('');

  useEffect(() => {
    fetchMarkdown();
  }, [actualFilePath]);

  const fetchMarkdown = async () => {
    if (!actualFilePath) {
      setErrorMessage('No file path provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);

      // Replace .ps1 with .md
      const mdFilePath = actualFilePath.replace(/\.ps1$/i, '.md');

      console.log('Fetching markdown from:', mdFilePath);

      // Call API with text response type
      const response = await jApi.request(
        'GET',
        `devops/repo/ves?filePath=${mdFilePath}`,
        null,
        { responseType: 'text' }
      );

      console.log('Markdown response:', response);

      // The response.data should contain the markdown text
      setMarkdownContent(response.data || '');
    } catch (error) {
      console.error('Failed to load markdown:', error);
      setErrorMessage('Failed to load documentation: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              {onClose && (
                <IconButton
                  onClick={onClose}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.6),
                    '&:hover': {
                      background: alpha(theme.palette.background.paper, 0.8),
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              {!onClose && (
                <IconButton
                  onClick={() => window.close()}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.6),
                    '&:hover': {
                      background: alpha(theme.palette.background.paper, 0.8),
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Audit Documentation
              </Typography>
            </Stack>
          </Box>
        </Fade>

        {errorMessage && (
          <Fade in>
            <Paper
              sx={{
                p: 2,
                mb: 3,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="error.main">
                {errorMessage}
              </Typography>
            </Paper>
          </Fade>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Loading documentation...
            </Typography>
          </Box>
        ) : markdownContent ? (
          <Fade in timeout={700}>
            <Paper
              sx={{
                p: 4,
                mb: 4,
                background: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 3,
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                  : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
              }}
            >
              <Box
                sx={{
                  '& h1': {
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    mb: 2,
                    mt: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  },
                  '& h2': {
                    fontSize: '2rem',
                    fontWeight: 600,
                    mb: 2,
                    mt: 3,
                    color: theme.palette.primary.main,
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    pb: 1,
                  },
                  '& h3': {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 1.5,
                    mt: 2.5,
                    color: theme.palette.text.primary,
                  },
                  '& h4': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    mb: 1,
                    mt: 2,
                    color: theme.palette.text.primary,
                  },
                  '& p': {
                    mb: 2,
                    lineHeight: 1.7,
                    color: theme.palette.text.secondary,
                  },
                  '& ul, & ol': {
                    mb: 2,
                    pl: 3,
                    '& li': {
                      mb: 0.5,
                      color: theme.palette.text.secondary,
                    },
                  },
                  '& code': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    px: 0.8,
                    py: 0.3,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                  },
                  '& pre': {
                    bgcolor: alpha(theme.palette.background.default, 0.8),
                    p: 2,
                    borderRadius: 2,
                    overflow: 'auto',
                    mb: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    '& code': {
                      bgcolor: 'transparent',
                      color: theme.palette.text.primary,
                      p: 0,
                    },
                  },
                  '& table': {
                    width: '100%',
                    mb: 2,
                    borderCollapse: 'collapse',
                    '& thead': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '& th': {
                        p: 1.5,
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        color: theme.palette.text.primary,
                      },
                    },
                    '& tbody': {
                      '& tr': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      },
                      '& td': {
                        p: 1.5,
                        color: theme.palette.text.secondary,
                      },
                    },
                  },
                  '& blockquote': {
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    p: 2,
                    pl: 3,
                    mb: 2,
                    borderRadius: 1,
                    '& p': {
                      mb: 0,
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    },
                  },
                  '& hr': {
                    border: 'none',
                    borderTop: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                    my: 3,
                  },
                  '& a': {
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {markdownContent}
                </ReactMarkdown>
              </Box>
            </Paper>
          </Fade>
        ) : null}
      </Container>
    </Box>
  );
}

export default Audit;
